import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  Check,
  X,
  AlertTriangle,
  Loader2,
  ChevronRight,
  ChevronDown,
  RotateCcw,
} from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import type { PipelineRunStatus } from '@jeraisonne/db';
import {
  usePipelineRun,
  usePipelineJobs,
  usePipelineLlmCalls,
  usePipelineReviewExercises,
  usePipelineNotifications,
} from '@/lib/admin/pipeline-queries';
import type { PipelineJobRow, PipelineRunRow } from '@/lib/admin/pipeline-queries';
import {
  useCancelRun,
  useResumeRun,
  useSetParallelLimit,
  useRetryJob,
} from '@/lib/admin/pipeline-mutations';
import {
  RUN_STATUS_LABEL,
  RUN_STATUS_CLS,
  runIsActive,
  jobStepLabel,
} from '@/lib/admin/pipeline-labels';
import {
  WORKER_STEPS,
  WORKER_STEP_LABELS,
  DEFAULT_STEP_MAX_TOKENS,
  MAX_TOKENS_MIN,
  MAX_TOKENS_CEILING,
  MAX_ITERATIONS_MIN,
  MAX_ITERATIONS_CEILING,
  type WorkerStep,
} from '@/lib/admin/pipeline-job-params';

/** Phases « heureuses » du run, dans l'ordre — pour la timeline. */
const PHASES: { status: PipelineRunStatus; short: string }[] = [
  { status: 'scenarios_generating', short: 'Scénarios' },
  { status: 'scenarios_auditing', short: 'Audit' },
  { status: 'awaiting_scenario_review', short: 'Validation 1' },
  { status: 'matching', short: 'Matching' },
  { status: 'awaiting_match_selection', short: 'Validation 2' },
  { status: 'generating', short: 'Génération' },
  { status: 'completed', short: 'Terminé' },
];

type Tab = 'overview' | 'jobs' | 'logs';

export function TutorAdminPipelineRunPage() {
  const { runId } = useParams({ strict: false }) as { runId: string };
  const { data: run, isLoading } = usePipelineRun(runId, { live: true });
  const { data: reviewExos } = usePipelineReviewExercises(runId);
  const cancel = useCancelRun();
  const resume = useResumeRun();
  const [tab, setTab] = useState<Tab>('overview');

  // Exercices encore à relire — les exercices déjà publiés/rejetés sont exclus.
  const pendingReview = (reviewExos ?? []).filter((e) => e.status === 'awaiting_review').length;

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl p-4 sm:p-6">
        <div className="bg-ink-100 h-40 animate-pulse rounded-2xl" />
      </div>
    );
  }
  if (!run) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <p className="text-ink-500 font-sans text-sm">Run introuvable.</p>
      </div>
    );
  }

  const phaseIndex = PHASES.findIndex((p) => p.status === run.status);
  const terminalBad = run.status === 'failed' || run.status === 'cancelled';

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <Link
        to="/tutor/admin/pipeline"
        className="text-ink-500 hover:text-ink-900 mb-4 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Pipeline
      </Link>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-display text-ink-900 text-2xl font-extrabold tracking-tight">
            Run du pipeline
          </h1>
          <span
            className={cn(
              'font-display mt-1.5 inline-flex h-6 items-center rounded-full border px-2.5 text-[11px] font-bold',
              RUN_STATUS_CLS[run.status],
            )}
          >
            {RUN_STATUS_LABEL[run.status]}
          </span>
        </div>
        {runIsActive(run.status) && (
          <Button
            variant="outline"
            onClick={() => cancel.mutate(runId)}
            disabled={cancel.isPending}
            className="min-h-11"
          >
            {cancel.isPending ? 'Annulation…' : 'Annuler le run'}
          </Button>
        )}
        {run.status === 'failed' && (
          <Button
            onClick={() => resume.mutate(runId)}
            disabled={resume.isPending}
            className="min-h-11"
          >
            {resume.isPending ? 'Reprise…' : 'Reprendre le run'}
          </Button>
        )}
      </div>

      {/* Timeline des phases */}
      <div className="border-ink-200 mb-5 flex items-center gap-1 overflow-x-auto rounded-2xl border bg-white p-3">
        {PHASES.map((p, i) => {
          const done = phaseIndex > i || run.status === 'completed';
          const current = phaseIndex === i && !terminalBad;
          return (
            <div key={p.status} className="flex shrink-0 items-center">
              <div className="flex flex-col items-center gap-1">
                <span className="relative flex h-6 w-6 items-center justify-center">
                  {current && run.status !== 'completed' && (
                    <span
                      aria-hidden
                      className="bg-primary-500 absolute inset-0 animate-ping rounded-full opacity-60"
                    />
                  )}
                  <span
                    className={cn(
                      'relative flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold',
                      done && 'bg-emerald-500 text-white',
                      current && 'bg-primary-600 text-white',
                      !done && !current && 'bg-ink-100 text-ink-400',
                    )}
                  >
                    {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                  </span>
                </span>
                <span
                  className={cn(
                    'whitespace-nowrap font-sans text-[10px]',
                    current ? 'text-primary-700 font-bold' : 'text-ink-400',
                  )}
                >
                  {p.short}
                </span>
              </div>
              {i < PHASES.length - 1 && <div className="bg-ink-200 mx-1 h-px w-6" />}
            </div>
          );
        })}
      </div>

      {terminalBad && run.error && (
        <div className="border-accent-200 bg-accent-50 mb-5 flex items-start gap-2 rounded-2xl border p-3">
          <AlertTriangle className="text-accent-600 mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0">
            {run.status === 'failed' && run.resume_from && (
              <p className="text-accent-800 font-display text-[12px] font-bold">
                Échec pendant la phase :{' '}
                {RUN_STATUS_LABEL[run.resume_from as PipelineRunStatus] ?? run.resume_from}
              </p>
            )}
            <p className="text-accent-700 font-sans text-[13px]">{run.error}</p>
          </div>
        </div>
      )}

      {/* Actions contextuelles */}
      <div className="mb-5 flex flex-wrap gap-2">
        {run.status === 'awaiting_scenario_review' && (
          <ActionLink to="/tutor/admin/pipeline/$runId/scenarios" runId={runId}>
            Valider les scénarios
          </ActionLink>
        )}
        {run.status === 'awaiting_match_selection' && (
          <ActionLink to="/tutor/admin/pipeline/$runId/matchings" runId={runId}>
            Valider les matchings
          </ActionLink>
        )}
        {pendingReview > 0 && (
          <ActionLink to="/tutor/admin/pipeline/$runId/review" runId={runId}>
            Relire les exercices ({pendingReview})
          </ActionLink>
        )}
      </div>

      {/* Onglets */}
      <div className="border-ink-200 mb-4 flex gap-1 border-b">
        {(
          [
            ['overview', 'Vue d’ensemble'],
            ['jobs', 'Jobs'],
            ['logs', 'Logs LLM'],
          ] as [Tab, string][]
        ).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={cn(
              'font-display -mb-px border-b-2 px-3 py-2 text-[13px] font-bold transition-colors',
              tab === id
                ? 'border-primary-600 text-primary-700'
                : 'text-ink-500 hover:text-ink-800 border-transparent',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab run={run} />}
      {tab === 'jobs' && <JobsTab run={run} />}
      {tab === 'logs' && <LogsTab runId={runId} />}
    </div>
  );
}

function ActionLink({
  to,
  runId,
  children,
}: {
  to: string;
  runId: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      params={{ runId }}
      className="bg-primary-600 font-display hover:bg-primary-700 inline-flex min-h-11 items-center rounded-xl px-4 text-[13px] font-bold text-white transition-all active:scale-[.98]"
    >
      {children}
    </Link>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="border-ink-200 rounded-xl border bg-white p-3">
      <p className="text-ink-400 font-sans text-[11px]">{label}</p>
      <p className="font-display text-ink-900 mt-0.5 text-[17px] font-extrabold">{value}</p>
    </div>
  );
}

/**
 * Contrôle éditable du parallélisme du run. Modifiable à tout moment tant que
 * le run n'est pas terminé — la nouvelle limite est appliquée immédiatement
 * (l'edge fn réveille la queue si le run est en phase de génération).
 */
function ParallelLimitControl({ run }: { run: PipelineRunRow }) {
  const setLimit = useSetParallelLimit();
  const editable = run.status !== 'completed' && run.status !== 'cancelled';
  const [pending, setPending] = useState<number | null>(null);
  const value = pending ?? run.parallel_limit;

  const apply = (next: number) => {
    if (next < 1 || next > 10 || next === run.parallel_limit) return;
    setPending(next);
    setLimit.mutate({ runId: run.id, parallelLimit: next }, { onSettled: () => setPending(null) });
  };

  return (
    <div className="border-ink-200 rounded-xl border bg-white p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-ink-400 font-sans text-[11px]">Parallélisme</p>
          <p className="font-display text-ink-900 mt-0.5 text-[17px] font-extrabold">
            {value} job{value > 1 ? 's' : ''} en parallèle
          </p>
        </div>
        {editable && (
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={() => apply(value - 1)}
              disabled={setLimit.isPending || value <= 1}
              aria-label="Réduire le parallélisme"
              className="border-ink-200 text-ink-700 flex h-11 w-11 items-center justify-center rounded-xl border bg-white text-xl font-bold transition-all active:scale-[.95] disabled:opacity-40"
            >
              −
            </button>
            <button
              type="button"
              onClick={() => apply(value + 1)}
              disabled={setLimit.isPending || value >= 10}
              aria-label="Augmenter le parallélisme"
              className="border-ink-200 text-ink-700 flex h-11 w-11 items-center justify-center rounded-xl border bg-white text-xl font-bold transition-all active:scale-[.95] disabled:opacity-40"
            >
              +
            </button>
          </div>
        )}
      </div>
      {setLimit.isError && (
        <p className="text-accent-600 mt-1.5 font-sans text-[11px]">
          {(setLimit.error as Error).message}
        </p>
      )}
    </div>
  );
}

function OverviewTab({ run }: { run: PipelineRunRow | null | undefined }) {
  if (!run) return null;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <Metric label="Jobs total" value={run.total_jobs} />
        <Metric label="Terminés" value={run.done_jobs} />
        <Metric label="Échoués" value={run.failed_jobs} />
        <Metric label="Abandonnés" value={run.abandoned_jobs} />
        <Metric label="Coût cumulé" value={`${run.total_cost_eur.toFixed(2)} €`} />
      </div>
      <ParallelLimitControl run={run} />
      <NotificationsCard runId={run.id} live={runIsActive(run.status)} />
      {run.particular_context && (
        <div className="border-ink-200 rounded-xl border bg-white p-3">
          <p className="text-ink-400 font-sans text-[11px]">Contexte particulier</p>
          <p className="text-ink-700 mt-1 font-sans text-[13px]">{run.particular_context}</p>
        </div>
      )}
      <p className="text-ink-400 font-sans text-[12px]">
        Lancé le {new Date(run.created_at).toLocaleString('fr-FR')}
        {run.completed_at && ` · terminé le ${new Date(run.completed_at).toLocaleString('fr-FR')}`}
      </p>
    </div>
  );
}

/** Libellés lisibles des événements de notification du pipeline. */
const NOTIF_EVENT_LABEL: Record<string, string> = {
  scenarios_ready: 'Scénarios à valider',
  matchings_ready: 'Matchings à valider',
  review_ready: 'Exercices à relire',
  fatal_error: 'Erreur fatale',
  budget_warning: 'Budget IA à 80 %',
  budget_exceeded: 'Budget IA dépassé',
  high_abandon_rate: 'Taux d’abandon élevé',
};

const CHANNEL_STATUS_LABEL: Record<string, string> = {
  sent: 'envoyé',
  failed: 'échec',
  skipped: 'ignoré',
};

/** Pastille de statut d'un canal de notification (mail ou push). */
function ChannelBadge({
  kind,
  status,
  error,
}: {
  kind: string;
  status: string | null;
  error: string | null;
}) {
  const s = status ?? 'skipped';
  const cls =
    s === 'sent'
      ? 'bg-emerald-50 text-emerald-700'
      : s === 'failed'
        ? 'bg-accent-50 text-accent-700'
        : 'bg-ink-100 text-ink-500';
  return (
    <span
      title={error ?? undefined}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-sans text-[11px] font-semibold',
        cls,
      )}
    >
      {s === 'sent' && <Check className="h-3 w-3" />}
      {s === 'failed' && <X className="h-3 w-3" />}
      {kind} {CHANNEL_STATUS_LABEL[s] ?? s}
    </span>
  );
}

/** Liste des notifications envoyées pour le run, avec le statut mail / push. */
function NotificationsCard({ runId, live }: { runId: string; live: boolean }) {
  const { data: notifs, isLoading } = usePipelineNotifications(runId, { live });
  return (
    <div className="border-ink-200 rounded-xl border bg-white p-3">
      <p className="font-display text-ink-700 mb-2 text-[12px] font-bold">Notifications</p>
      {isLoading ? (
        <p className="text-ink-400 font-sans text-[12px]">Chargement…</p>
      ) : (notifs ?? []).length === 0 ? (
        <p className="text-ink-400 font-sans text-[12px]">
          Aucune notification envoyée pour l’instant.
        </p>
      ) : (
        <ul className="space-y-2.5">
          {(notifs ?? []).map((n) => (
            <li key={n.id} className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-ink-800 mr-1 font-sans text-[12px]">
                  {NOTIF_EVENT_LABEL[n.event] ?? n.event}
                </span>
                <ChannelBadge kind="Mail" status={n.email_status} error={n.email_error} />
                <ChannelBadge kind="Push" status={n.push_status} error={n.push_error} />
              </div>
              {n.email_status === 'failed' && n.email_error && (
                <p className="text-accent-600 font-mono text-[10px] leading-tight">
                  Mail : {n.email_error}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const JOB_STATUS_CLS: Record<string, string> = {
  queued: 'bg-ink-100 text-ink-600',
  processing: 'bg-primary-50 text-primary-700',
  done: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-accent-50 text-accent-700',
  abandoned: 'bg-amber-50 text-amber-700',
  cancelled: 'bg-ink-100 text-ink-500',
};

/**
 * Ordre d'affichage des jobs, du plus utile (réclame une action / en cours) au
 * moins utile (terminé / annulé). À statut égal, on trie ensuite par date de
 * fin (cf. `sortPipelineJobs`).
 */
const JOB_STATUS_ORDER: Record<string, number> = {
  processing: 0,
  queued: 1,
  failed: 2,
  abandoned: 3,
  done: 4,
  cancelled: 5,
};

/**
 * Tri d'affichage des jobs : d'abord par statut (action requise → terminé),
 * puis, à statut égal, du plus récemment terminé au plus ancien. Les jobs sans
 * date de fin (queued / processing) restent en tête de leur groupe.
 */
export function sortPipelineJobs(jobs: PipelineJobRow[]): PipelineJobRow[] {
  return [...jobs].sort((a, b) => {
    const byStatus = (JOB_STATUS_ORDER[a.status] ?? 99) - (JOB_STATUS_ORDER[b.status] ?? 99);
    if (byStatus !== 0) return byStatus;
    const ta = a.finished_at ? Date.parse(a.finished_at) : 0;
    const tb = b.finished_at ? Date.parse(b.finished_at) : 0;
    return tb - ta;
  });
}

function JobsTab({ run }: { run: PipelineRunRow }) {
  const { data: jobs, isLoading } = usePipelineJobs(run.id, { live: true });
  if (isLoading) return <div className="bg-ink-100 h-32 animate-pulse rounded-2xl" />;
  if ((jobs?.length ?? 0) === 0) {
    return (
      <p className="text-ink-500 font-sans text-sm">Aucun job (matchings pas encore lancés).</p>
    );
  }
  const sortedJobs = sortPipelineJobs(jobs ?? []);
  return (
    <div className="space-y-2">
      {sortedJobs.map((job) => (
        <JobRow key={job.id} job={job} run={run} />
      ))}
    </div>
  );
}

function JobRow({ job, run }: { job: PipelineJobRow; run: PipelineRunRow }) {
  // Un job `done` a produit un exercice → la ligne devient cliquable pour
  // l'ouvrir directement (relecture / vérification).
  const exerciseId = job.status === 'done' ? job.exercise_id : null;
  const retryable = job.status === 'failed' || job.status === 'abandoned';

  const content = (
    <>
      <span
        className={cn(
          'font-display inline-flex h-5 shrink-0 items-center rounded-full px-2 text-[10px] font-bold uppercase',
          JOB_STATUS_CLS[job.status] ?? 'bg-ink-100 text-ink-500',
        )}
      >
        {job.status}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-ink-800 truncate font-sans text-[13px]">
          {job.match?.chapter?.title ?? 'Chapitre'}
          {job.match?.lesson?.title && (
            <span className="text-ink-400"> · {job.match.lesson.title}</span>
          )}
        </p>
        {job.status === 'processing' && job.current_step && (
          <p className="text-primary-600 mt-0.5 flex items-center gap-1 font-sans text-[11px]">
            <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
            {jobStepLabel(job.current_step)} · énoncé {job.statement_iterations} / corrigé{' '}
            {job.solution_iterations}
          </p>
        )}
        {job.finished_at && (
          <p className="text-ink-400 mt-0.5 font-sans text-[11px]">
            Terminé le {new Date(job.finished_at).toLocaleString('fr-FR')}
          </p>
        )}
      </div>
      <span className="text-ink-400 shrink-0 font-mono text-[11px]">
        {job.cost_eur.toFixed(2)} €
      </span>
      {exerciseId && <ChevronRight className="text-ink-400 h-4 w-4 shrink-0" />}
    </>
  );

  // Job en erreur / abandonné : ligne d'info + erreur complète dans un textarea
  // scrollable + panneau de relance manuelle.
  if (job.error || retryable) {
    const failedStep = jobStepLabel(job.current_step);
    return (
      <div className="border-ink-200 flex flex-col gap-2 rounded-xl border bg-white p-3">
        <div className="flex items-center gap-3">{content}</div>
        {failedStep && (
          <p className="text-accent-700 font-sans text-[12px]">
            <span className="font-display font-bold">Échec à l’étape :</span> {failedStep}
          </p>
        )}
        {job.error && (
          <label className="flex flex-col gap-1">
            <span className="text-accent-700 font-display text-[11px] font-bold">
              Erreur complète
            </span>
            <textarea
              readOnly
              value={job.error}
              rows={5}
              className="border-accent-200 bg-accent-50/60 text-accent-800 w-full resize-y rounded-lg border p-2 font-mono text-[11px] leading-snug"
            />
          </label>
        )}
        {retryable && <JobArtifacts job={job} />}
        {retryable && <JobRetryPanel job={job} run={run} />}
      </div>
    );
  }

  if (exerciseId) {
    return (
      <Link
        to="/tutor/exercises/$exerciseId"
        params={{ exerciseId }}
        className="border-ink-200 hover:border-primary-300 flex min-h-11 items-center gap-3 rounded-xl border bg-white p-3 transition-all active:scale-[.99]"
      >
        {content}
      </Link>
    );
  }
  return (
    <div className="border-ink-200 flex min-h-11 items-center gap-3 rounded-xl border bg-white p-3">
      {content}
    </div>
  );
}

/**
 * Travail déjà produit par le job (dernier énoncé / corrigé / distracteurs /
 * retour de vérification) — pour décider en connaissance de cause s'il faut
 * relancer ou non. Affiché en JSON brut, scrollable.
 */
function JobArtifacts({ job }: { job: PipelineJobRow }) {
  const [open, setOpen] = useState(false);
  const items = (
    [
      ['Énoncé', job.last_statement],
      ['Corrigé', job.last_solution],
      ['Distracteurs', job.last_distractors],
      ['Retour de vérification', job.last_verification_feedback],
    ] as [string, unknown][]
  ).filter(([, v]) => v != null);
  if (items.length === 0) return null;

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-ink-600 hover:text-ink-900 inline-flex min-h-11 items-center gap-1.5 self-start font-sans text-[12px] font-semibold"
      >
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        Travail produit ({items.length})
      </button>
      {open &&
        items.map(([label, value]) => (
          <label key={label} className="flex flex-col gap-0.5">
            <span className="text-ink-500 font-display text-[11px] font-bold">{label}</span>
            <textarea
              readOnly
              rows={6}
              value={JSON.stringify(value, null, 2)}
              className="border-ink-200 bg-ink-50 text-ink-700 w-full resize-y rounded-lg border p-2 font-mono text-[11px] leading-snug"
            />
          </label>
        ))}
    </div>
  );
}

/**
 * Panneau de relance manuelle d'un job `failed` / `abandoned`. L'admin peut
 * ajuster les `max_tokens` de chaque étape (utile quand une réponse a tronqué)
 * et le nombre d'itérations avant de remettre le job en queue.
 */
function JobRetryPanel({ job, run }: { job: PipelineJobRow; run: PipelineRunRow }) {
  const retry = useRetryJob();
  const [open, setOpen] = useState(false);
  const [tokens, setTokens] = useState<Record<WorkerStep, number>>(DEFAULT_STEP_MAX_TOKENS);
  const [iterStatement, setIterStatement] = useState(run.max_iterations_statement);
  const [iterSolution, setIterSolution] = useState(run.max_iterations_solution);

  const numCls =
    'border-ink-200 focus:ring-primary-400 min-h-11 w-full rounded-lg border px-2 font-mono text-[12px] focus:outline-none focus:ring-2';

  function submit() {
    retry.mutate(
      {
        runId: run.id,
        jobId: job.id,
        overrides: {
          maxTokens: tokens,
          maxIterationsStatement: iterStatement,
          maxIterationsSolution: iterSolution,
        },
      },
      { onSuccess: () => setOpen(false) },
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="border-ink-200 text-ink-700 hover:bg-ink-50 font-display inline-flex min-h-11 items-center gap-1.5 self-start rounded-xl border px-3 text-[12px] font-bold transition-colors active:scale-[.98]"
      >
        <RotateCcw className="h-3.5 w-3.5" />
        Réessayer ce job
      </button>
    );
  }

  return (
    <div className="border-ink-200 bg-ink-50/60 flex flex-col gap-3 rounded-xl border p-3">
      <p className="text-ink-600 font-sans text-[12px]">
        Ajuste les <span className="font-semibold">tokens max</span> d’une étape qui a tronqué sa
        réponse, puis relance. Le job repart en file d’attente.
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {WORKER_STEPS.map((s) => (
          <label key={s} className="flex flex-col gap-0.5">
            <span className="text-ink-500 font-sans text-[11px]">
              {WORKER_STEP_LABELS[s]} — tokens
            </span>
            <input
              type="number"
              min={MAX_TOKENS_MIN}
              max={MAX_TOKENS_CEILING}
              step={500}
              value={tokens[s]}
              onChange={(e) =>
                setTokens((p) => ({
                  ...p,
                  [s]: Number(e.target.value) || DEFAULT_STEP_MAX_TOKENS[s],
                }))
              }
              className={numCls}
            />
          </label>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-0.5">
          <span className="text-ink-500 font-sans text-[11px]">Itérations énoncé</span>
          <input
            type="number"
            min={MAX_ITERATIONS_MIN}
            max={MAX_ITERATIONS_CEILING}
            value={iterStatement}
            onChange={(e) => setIterStatement(Number(e.target.value) || MAX_ITERATIONS_MIN)}
            className={numCls}
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-ink-500 font-sans text-[11px]">Itérations corrigé</span>
          <input
            type="number"
            min={MAX_ITERATIONS_MIN}
            max={MAX_ITERATIONS_CEILING}
            value={iterSolution}
            onChange={(e) => setIterSolution(Number(e.target.value) || MAX_ITERATIONS_MIN)}
            className={numCls}
          />
        </label>
      </div>
      {retry.isError && (
        <p className="text-accent-600 font-sans text-[12px]">{(retry.error as Error).message}</p>
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={() => setOpen(false)} className="min-h-11">
          Annuler
        </Button>
        <Button type="button" onClick={submit} disabled={retry.isPending} className="min-h-11">
          {retry.isPending ? 'Relance…' : 'Relancer le job'}
        </Button>
      </div>
    </div>
  );
}

function LogsTab({ runId }: { runId: string }) {
  const { data: calls, isLoading } = usePipelineLlmCalls(runId);
  if (isLoading) return <div className="bg-ink-100 h-32 animate-pulse rounded-2xl" />;
  if ((calls?.length ?? 0) === 0) {
    return <p className="text-ink-500 font-sans text-sm">Aucun appel LLM enregistré.</p>;
  }
  return (
    <div className="space-y-1.5">
      {(calls ?? []).map((c) => (
        <div
          key={c.id}
          className="border-ink-200 flex items-center gap-2 rounded-lg border bg-white px-3 py-2"
        >
          {c.success ? (
            <Check className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
          ) : (
            <X className="text-accent-500 h-3.5 w-3.5 shrink-0" />
          )}
          <span className="font-display text-ink-800 w-44 shrink-0 truncate text-[12px] font-semibold">
            {c.step}
          </span>
          <span className="text-ink-400 hidden shrink-0 font-mono text-[11px] sm:inline">
            {c.model}
          </span>
          <span className="text-ink-500 ml-auto shrink-0 font-mono text-[11px]">
            {c.duration_ms != null ? `${c.duration_ms} ms` : '—'} ·{' '}
            {c.cost_eur != null ? `${c.cost_eur.toFixed(3)} €` : '—'}
          </span>
        </div>
      ))}
    </div>
  );
}
